import React, { useEffect, useRef, useState } from 'react';

import altBackground from '../../assets/images/events/goal-background.png';
import { events, LbEvent, LbEventType } from '../../configs/events';
import { ComponentProps } from '../../typings/generic-component';
import { LiveblogTenant } from '../../typings/liveblog-themes';
import { IClassNames, makeClassNames } from '../../utils/styles';
import EventIcon from '../event-icon/event-icon';
import { AcceptedCookiesConfig } from '../liveblog/liveblog';
import PostImage, { IPostImage } from '../post-image/post-image';
import PostVideo, { IPostVideo } from '../post-video/post-video';
import SocialEmbed, { ISocialEmbed } from '../social-embed/social-embed';
import PostTimestamp from '../post-timestamp/post-timestamp';
import './post.scss';
import { getDateTime } from '../../utils/times';
import { initDatalayerElement } from '../../utils/tracking-data';
import IconArrowDown from '../icons/icon-arrow-down';
import IconArrowUp from '../icons/icon-arrow-up';
import { isMobile } from '../../utils/device-utils';
import { IPostLink, PostLink } from '../index';
import { stripHtmlTags } from '../../utils/post-utils';

const DESKTOP_MAX_CONTENT_LENGTH = 500;
const MOBILE_MAX_CONTENT_LENGTH = 380;

export interface IPost extends ComponentProps {
  postId: string;
  isPinned?: boolean;
  title?: string;
  content?: string;
  timestamp: string; // ISO format like '2022-10-18T09:01:22.423Z'
  timestampUtcIt?: string; // ISO format like '2022-10-18T11:01:22.423+02:00'
  event?: LbEvent;
  altBackground?: boolean;
  authorName?: string;
  video?: IPostVideo;
  image?: IPostImage;
  social?: ISocialEmbed;
  dateNow?: Date;
  acceptedCookiesConfig?: AcceptedCookiesConfig;
  tenant?: LiveblogTenant;
  isWebView?: boolean;
  isObserved?: boolean;
  pageNumber?: number;
  isHidden?: boolean;
  postLink?: IPostLink;
}

const classNames = (
  event?: LbEvent,
  altBackground?: boolean,
  isWebView?: boolean,
  isGol?: boolean,
  isCheckedFlag?: boolean,
  isVideoOrImage?: boolean,
  isSkyPost?: boolean,
  hasReadmore?: boolean,
  isReadmoreOpen?: boolean
): IClassNames =>
  makeClassNames({
    Post: 'post',
    Post_Wrapper: 'post__wrapper',
    Post_Icon: 'post__icon',
    Post_Container: 'post__container',
    Post_Title: [(isGol || isCheckedFlag) && 'post__title-alt', 'post__title-v2'],
    Post_Content: [
      event && !isSkyPost && 'post__event-post-content',
      isSkyPost && 'post__sky-post-content',
      (isGol || isCheckedFlag) && ' post__event-post-content-alt post__content-alt',
      altBackground && !event && !isWebView && 'post__content--with-alt-background',
      'post__content-v2',
    ],
    Post_ContentText: 'post__content-text-v2',
    Post__Readmore: 'post__readmore',
    Post_Body: [
      'post__body',
      (isGol || isCheckedFlag) && isVideoOrImage && 'post__body-alt',
      hasReadmore && 'post__body--with-readmore',
      hasReadmore && isReadmoreOpen && 'post__body--readmore-open',
    ],
    Post_Event_Container: ['post__event__container', isSkyPost && 'post__event__container--sky-post'],
    Post_Event_IconTitle: [
      isGol && 'post__event__icon-title-gol',
      isCheckedFlag && 'post__event__icon-title-checked',
      event?.type === LbEventType.SPECIAL_EVENTS && 'post__event__icon-title-capitalizeFirstLetter',
      'post__event__icon-title',
    ],
    Post_Author: 'post__author',
    Post_AuthorName: 'post__author-name',
    Post_Footer: 'post__footer',
  });

const altBackgroundStyles = {
  backgroundRepeat: 'no-repeat',
  backgroundSize: 'contain',
  backgroundImage: `url(${altBackground})`,
  color: 'white',
};

const Post: React.FC<IPost> = ({
  postId,
  content = '',
  title,
  timestamp,
  timestampUtcIt,
  event,
  altBackground = false,
  video,
  image,
  social,
  dateNow,
  acceptedCookiesConfig,
  tenant,
  isWebView,
  isHidden,
  postLink,
}) => {
  const postContentRef = useRef<HTMLDivElement | null>(null);
  const postBodyRef = useRef<HTMLDivElement | null>(null);
  const LMP_BORDER_COLOR = event?.borderColor || '';
  const isGol: boolean = event?.text === events?.goal.text;
  const isCheckedFlag: boolean = event?.text === events?.['bandiera a scacchi'].text;
  const isVideoOrImage: boolean = !!(video || image);
  const { socialType, src: socialSrc } = social || {};
  const isSkyPost: boolean = event?.type === LbEventType.SKY_POST;
  const isEventIconHidden: boolean = (event && event.isIconHidden) || false;
  const [isReadmoreOpen, setReadmoreOpen] = useState(false);

  const { time, date } = getDateTime({ timestamp, dateNow });

  const hasBackgroundColored = isGol || isCheckedFlag;
  const rawContentLength = content ? stripHtmlTags(content).length : 0;
  const hasReadmore =
    !video &&
    !image?.imgSrc &&
    !socialSrc &&
    !hasBackgroundColored &&
    !postLink?.title &&
    rawContentLength > (isMobile() ? MOBILE_MAX_CONTENT_LENGTH : DESKTOP_MAX_CONTENT_LENGTH);

  const cn = classNames(
    event,
    altBackground,
    isWebView,
    isGol,
    isCheckedFlag,
    isVideoOrImage,
    isSkyPost,
    hasReadmore,
    isReadmoreOpen
  );

  useEffect(() => {
    if (!isHidden && event && postContentRef.current) {
      const isAltBackgroundPost = isGol || isCheckedFlag;
      postContentRef.current.style.setProperty(
        '--lmpBorderColor',
        isAltBackgroundPost ? (event?.backgroundColor as string) : LMP_BORDER_COLOR
      );
      if (isAltBackgroundPost) {
        Object.assign(postContentRef.current.style, altBackgroundStyles);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isHidden]);

  useEffect(() => {
    if (!isHidden) {
      initDatalayerElement(postContentRef.current);
    }
  }, [content, isHidden]);

  return (
    <div className={cn.Post}>
      <PostTimestamp timestampUtcIt={timestampUtcIt} timestamp={timestamp} time={time} date={date} />
      <div className={cn.Post_Wrapper}>
        <div className={cn.Post_Container}>
          <div ref={postContentRef} className={cn.Post_Content}>
            {event && !isEventIconHidden && !isSkyPost && (
              <div className={cn.Post_Event_Container}>
                <EventIcon {...event} />
                <span className={cn.Post_Event_IconTitle}>{event.text}</span>
              </div>
            )}
            <div className={cn.Post_ContentText}>
              {isSkyPost && !isEventIconHidden ? (
                <div className={cn.Post_Event_Container}>
                  <EventIcon {...event} />
                  <h2 className={cn.Post_Title}>{title}</h2>
                </div>
              ) : (
                <h2 className={cn.Post_Title}>{title}</h2>
              )}

              <div className={cn.Post_Body} style={isReadmoreOpen ? { height: postBodyRef.current?.clientHeight } : {}}>
                <div ref={postBodyRef} dangerouslySetInnerHTML={{ __html: content }} />
              </div>
              {hasReadmore && (
                <button
                  type="button"
                  className={cn.Post__Readmore}
                  onClick={() => setReadmoreOpen((isOpen) => !isOpen)}
                >
                  {isReadmoreOpen ? (
                    <>
                      Leggi di meno <IconArrowUp width={10} />
                    </>
                  ) : (
                    <>
                      Leggi di più <IconArrowDown width={10} />
                    </>
                  )}
                </button>
              )}
            </div>
            {!isHidden && video && (
              <PostVideo
                {...video}
                containerId={`lvbg-video-player-post-${postId}`}
                autostart={false}
                tenant={tenant}
                isWebView={isWebView}
              />
            )}
            {image?.imgSrc && <PostImage {...image} />}
            {!isHidden && socialType && socialSrc && acceptedCookiesConfig && (
              <SocialEmbed
                {...social}
                src={socialSrc}
                cookieAllowed={acceptedCookiesConfig[socialType]}
                isWebView={isWebView}
              />
            )}
            {postLink?.title && (
              <PostLink
                tenant={tenant as LiveblogTenant}
                title={postLink.title}
                imageSrc={postLink.imageSrc}
                url={postLink.url}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Post;
